'use client';
import React from 'react';
import { OrderSummary as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { useCart } from '@/lib/hooks/useCart';
import { convertToLocale } from '@lib/util/money';

const SharedRender = (Shared as any).render;

const mapItem = (i: any) => ({
  id: i.id,
  title: i.title ?? i.product_title ?? '',
  product_title: i.product_title ?? i.title ?? '',
  product_handle: i.product?.handle ?? i.variant?.product?.handle ?? '',
  thumbnail: i.thumbnail,
  variant: {
    id: i.variant_id ?? i.variant?.id ?? '',
    title: i.variant?.title ?? i.variant_title ?? '',
    options: (i.variant?.options ?? []).map((o: any) => ({ value: o.value ?? o.option?.value ?? '' })),
  },
  quantity: i.quantity ?? 1,
  unit_price: Number(i.unit_price) || 0,
  total: Number(i.total ?? (Number(i.unit_price || 0) * (i.quantity ?? 1))),
  isPreorder: i.metadata?.is_preorder === true,
});

/**
 * Storefront OrderSummary — like CartItems but for the checkout-side
 * summary. Injects items + totals + onQuantityChange/onDelete. Prefers
 * `context.order` over `context.cart` so it works on order confirmation
 * pages too.
 */
function OrderSummaryImpl(props: any) {
  const { context } = usePuckContext() as any;
  const { updateQuantity, removeItem } = useCart() as any;
  const src = context?.order ?? context?.cart;

  if (!src) {
    return <SharedRender {...props} items={undefined} totals={undefined} />;
  }

  const currency = src.currency_code || 'usd';
  const items = (src.items ?? []).map(mapItem);
  const totals = {
    subtotal: Number(src.subtotal) || 0,
    shipping: Number(src.shipping_total) || 0,
    tax: Number(src.tax_total) || 0,
    discount: Number(src.discount_total) || 0,
    total: Number(src.total) || 0,
  };
  const formatPrice = (price: number) =>
    convertToLocale({ amount: Number(price) || 0, currency_code: currency });

  // Only wire onQuantityChange/onDelete on the cart (not on completed orders)
  const isOrder = !!context?.order;
  const handlers = isOrder
    ? {}
    : {
        onQuantityChange: (itemId: string, qty: number) => updateQuantity(itemId, qty),
        onDelete: (itemId: string) => removeItem(itemId),
      };

  return (
    <SharedRender
      {...props}
      items={items}
      totals={totals}
      formatPrice={formatPrice}
      {...handlers}
    />
  );
}

export const OrderSummary: any = {
  ...Shared,
  render: (props: any) => <OrderSummaryImpl {...props} />,
};
export default OrderSummary;
